import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface SlideProps {
  onNext?: () => void;
}
export function Slide01Hero({
  onNext
}: SlideProps) {
  const metrics = [{
    label: 'Attention',
    x: -120,
    y: -80,
    delay: 0
  }, {
    label: 'Understanding',
    x: 140,
    y: -60,
    delay: 0.2
  }, {
    label: 'Trust',
    x: -140,
    y: 60,
    delay: 0.4
  }, {
    label: 'Intent',
    x: 120,
    y: 80,
    delay: 0.6
  }, {
    label: 'Curiosity',
    x: 0,
    y: -130,
    delay: 0.8
  }];
  return <div className="w-full h-full flex items-center justify-between max-w-7xl mx-auto" data-id="element-1">
      {/* Left Content */}
      <div className="w-1/2 pr-12 z-10" data-id="element-2">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-3">
          <h1 className="text-[clamp(3rem,5vw,5.5rem)] leading-[1.05] font-light tracking-tight mb-6 text-snakker-ink" data-id="element-4">
            Beyond Clicks.
            <br data-id="element-5" />
            <span className="text-gradient" data-id="element-6">Understanding Behavior.</span>
          </h1>

          <p className="text-xl text-snakker-muted font-light mb-12 max-w-lg leading-relaxed" data-id="element-7">
            <span className="text-snakker-ink font-medium" data-id="element-8">Snakker™</span>{' '}
            turns engagement into intelligence.
          </p>

          <motion.button onClick={onNext} className="group flex items-center gap-3 bg-snakker-ink text-white px-8 py-4 rounded-full font-medium tracking-wide hover:bg-snakker-primary transition-colors shadow-lg" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-9">
            Explore the framework
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-10" />
          </motion.button>
        </motion.div>
      </div>

      {/* Right Visual - Intelligence Engine */}
      <div className="w-1/2 h-full relative flex items-center justify-center" data-id="element-11">
        <div className="relative w-[500px] h-[500px]" data-id="element-12">
          {/* Central Core */}
          <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-br from-snakker-primary to-snakker-secondary blur-md opacity-50" animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.7, 0.4]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-13" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-white border border-snakker-border flex items-center justify-center shadow-[0_8px_40px_rgba(0,133,189,0.25)] z-20" data-id="element-14">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-snakker-primary to-snakker-secondary" data-id="element-15" />
          </div>

          {/* Network Lines */}
          <svg className="absolute inset-0 w-full h-full z-0" data-id="element-16">
            <defs data-id="element-17">
              <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-18">
                <stop offset="0%" stopColor="rgba(13,15,20,0)" data-id="element-19" />
                <stop offset="50%" stopColor="rgba(0,133,189,0.5)" data-id="element-20" />
                <stop offset="100%" stopColor="rgba(124,92,245,0.7)" data-id="element-21" />
              </linearGradient>
            </defs>
            {[...Array(12)].map((_, i) => {
            const angle = i * 30 * (Math.PI / 180);
            const x1 = 250 + Math.cos(angle) * 220;
            const y1 = 250 + Math.sin(angle) * 220;
            return <motion.path key={i} d={`M ${x1} ${y1} Q ${250 + Math.cos(angle) * 100} ${250 + Math.sin(angle) * 100} 250 250`} fill="none" stroke="url(#lineGrad)" strokeWidth="1.5" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: 0.6
            }} transition={{
              duration: 2,
              delay: i * 0.1,
              ease: 'easeOut'
            }} data-id="element-22" />;
          })}

            {/* Animated particles traveling to center */}
            {[...Array(8)].map((_, i) => {
            const angle = (i * 45 + 15) * (Math.PI / 180);
            const startX = 250 + Math.cos(angle) * 200;
            const startY = 250 + Math.sin(angle) * 200;
            return <motion.circle key={`p-${i}`} r="3" fill="#0085bd" initial={{
              cx: startX,
              cy: startY,
              opacity: 0
            }} animate={{
              cx: 250,
              cy: 250,
              opacity: [0, 1, 0]
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              delay: i * 0.4,
              ease: 'easeIn'
            }} data-id="element-23" />;
          })}
          </svg>

          {/* Floating Metric Pills */}
          {metrics.map((metric, i) => <motion.div key={metric.label} className="absolute top-1/2 left-1/2 px-4 py-2 rounded-full bg-white border border-snakker-border shadow-md text-sm text-snakker-ink whitespace-nowrap z-30" initial={{
          x: metric.x,
          y: metric.y,
          opacity: 0,
          scale: 0.8
        }} animate={{
          x: metric.x,
          y: metric.y,
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1 + metric.delay
        }} data-id="element-24">
              <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            delay: metric.delay,
            ease: 'easeInOut'
          }} data-id="element-25">
                {metric.label}
              </motion.div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}